/**
 * Rebuilds a page's visual layout from OCR word boxes.
 *
 * OCR engines return text in reading order, block by block, which scrambles a
 * form: on a typical Tally invoice the labels "Invoice No." and "Dated" sit in
 * one row and their values in the row below, beside the supplier's address, so
 * the plain text reads "72,SARATHY NAGAR SBC/3804/2025-26 3-Mar-26" with no
 * way to tell which value belongs to which label.
 *
 * Laying every word out on a character grid at its position on the page keeps
 * that structure: words in the same row share a line, and a value printed
 * under a label starts in the same column as the label.
 */
/** Blank lines kept between rows that are visually far apart, at most. */
const MAX_BLANK_LINES = 1;
export function layoutText(words) {
    const usable = words.filter((word) => word.text.trim() && word.x1 > word.x0 && word.y1 > word.y0);
    if (!usable.length)
        return '';
    const charWidth = median(usable.map((word) => (word.x1 - word.x0) / word.text.length));
    const lineHeight = median(usable.map((word) => word.y1 - word.y0));
    const left = Math.min(...usable.map((word) => word.x0));
    const rows = groupRows(usable, lineHeight);
    const lines = [];
    let previousBottom;
    for (const row of rows) {
        const top = Math.min(...row.map((word) => word.y0));
        if (previousBottom !== undefined) {
            const gaps = Math.floor((top - previousBottom) / (lineHeight * 1.5));
            for (let blank = 0; blank < Math.min(gaps, MAX_BLANK_LINES); blank++)
                lines.push('');
        }
        previousBottom = Math.max(...row.map((word) => word.y1));
        let line = '';
        for (const word of row.sort((a, b) => a.x0 - b.x0)) {
            const column = Math.round((word.x0 - left) / charWidth);
            // Words never touch: at least one space, even where OCR boxes overlap.
            const start = Math.max(column, line.length ? line.length + 1 : 0);
            line = line.padEnd(start) + word.text.trim();
        }
        lines.push(line);
    }
    return lines.join('\n');
}
/**
 * Words whose vertical centres fall within half a line of a row's centre join
 * that row, so a slightly skewed scan still keeps one printed line together.
 */
function groupRows(words, lineHeight) {
    const byCentre = [...words].sort((a, b) => centre(a) - centre(b));
    const rows = [];
    for (const word of byCentre) {
        const row = rows.at(-1);
        if (row && Math.abs(centre(word) - row.centre) <= lineHeight / 2) {
            row.words.push(word);
            row.centre = row.words.reduce((sum, member) => sum + centre(member), 0) / row.words.length;
        }
        else {
            rows.push({ centre: centre(word), words: [word] });
        }
    }
    return rows.map((row) => row.words);
}
function centre(word) {
    return (word.y0 + word.y1) / 2;
}
function median(values) {
    const sorted = [...values].sort((a, b) => a - b);
    const middle = Math.floor(sorted.length / 2);
    return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}
//# sourceMappingURL=ocrLayout.js.map